import { useEffect, useState } from "react";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Scale, Save, Calculator, Plus } from "lucide-react";

const GROUPS = [
  {
    title: "Prestaciones de Ley (LFT)",
    fields: [
      ["aguinaldo_dias", "Aguinaldo (días)"],
      ["dias_vacaciones", "Vacaciones (días)"],
      ["prima_vacacional_pct", "Prima vacacional (%)"],
      ["prima_dominical_pct", "Prima dominical (%)"],
    ],
  },
  {
    title: "Días del Año",
    fields: [
      ["dias_calendario", "Días calendario"],
      ["dias_domingos", "Domingos"],
      ["dias_festivos", "Días festivos"],
      ["dias_vacaciones_no_lab", "Vacaciones no laboradas"],
      ["dias_no_laborados_otros", "Otros no laborados"],
    ],
  },
  {
    title: "Cuotas Patronales",
    fields: [
      ["imss_patronal_pct", "IMSS patronal (%)"],
      ["infonavit_pct", "INFONAVIT (%)"],
      ["sar_pct", "SAR (%)"],
      ["impuesto_nomina_pct", "Impuesto sobre nómina (%)"],
    ],
  },
  {
    title: "Referencias Económicas",
    fields: [
      ["uma_diaria", "UMA diaria"],
      ["salario_minimo_general", "Salario mínimo general"],
      ["salario_minimo_frontera", "Salario mínimo frontera"],
    ],
  },
];

export default function FasarView() {
  const { user } = useAuth();
  const canWrite = user?.role !== "consulta";
  const [years, setYears] = useState([]);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [calc, setCalc] = useState({ salario: 480, result: null });

  const load = async () => {
    const { data } = await api.get("/fasar");
    setYears(data);
    setCurrent((c) => data.find((d) => d.anio === c?.anio) || data[0] || null);
  };
  useEffect(() => {
    load();
  }, []);

  const setField = (k) => (e) =>
    setCurrent((c) => ({ ...c, [k]: parseFloat(e.target.value) || 0 }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/fasar", current);
      setCurrent(data);
      toast.success(`Parámetros FASAR ${data.anio} guardados`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const newYear = () => {
    const y = (years[0]?.anio || 2026) + 1;
    if (current) setCurrent({ ...current, id: undefined, anio: y, descripcion: `Parámetros FASAR ${y}` });
  };

  const runCalc = async () => {
    try {
      const { data } = await api.post("/fasar/calcular", { anio: current.anio, salario_diario: parseFloat(calc.salario) || 0 });
      setCalc((c) => ({ ...c, result: data }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="h-12 shrink-0 border-b border-border flex items-center px-3 gap-2">
        <Scale className="h-4 w-4 text-primary" />
        <h2 className="font-head font-bold">Motor Normativo FASAR</h2>
        <div className="flex-1" />
        <Select value={String(current?.anio || "")} onValueChange={(v) => setCurrent(years.find((y) => String(y.anio) === v))}>
          <SelectTrigger className="w-28 h-8 rounded-sm font-mono-num" data-testid="fasar-year-select"><SelectValue placeholder="Año" /></SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y.anio} value={String(y.anio)} className="font-mono-num">{y.anio}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canWrite && (
          <>
            <Button variant="outline" size="sm" className="h-8 rounded-sm gap-1" onClick={newYear} data-testid="fasar-new-year"><Plus className="h-4 w-4" /> Año</Button>
            <Button size="sm" className="h-8 rounded-sm gap-1" onClick={save} disabled={saving} data-testid="fasar-save"><Save className="h-4 w-4" /> Guardar</Button>
          </>
        )}
      </div>

      {current ? (
        <div className="flex-1 overflow-auto p-4 grid gap-4 xl:grid-cols-3">
          <div className="xl:col-span-2 grid sm:grid-cols-2 gap-4 content-start">
            {GROUPS.map((g) => (
              <div key={g.title} className="border border-border rounded-sm">
                <div className="px-3 h-9 flex items-center border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">{g.title}</div>
                <div className="p-3 grid gap-2">
                  {g.fields.map(([k, label]) => (
                    <div key={k} className="flex items-center justify-between gap-2">
                      <Label className="text-xs text-muted-foreground">{label}</Label>
                      <Input
                        type="number"
                        step="any"
                        disabled={!canWrite}
                        value={current[k]}
                        onChange={setField(k)}
                        className="w-28 h-8 text-right font-mono-num rounded-sm"
                        data-testid={`fasar-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Calculadora */}
          <div className="border border-primary/40 rounded-sm h-fit">
            <div className="px-3 h-9 flex items-center gap-2 border-b border-border bg-primary/10 text-xs uppercase tracking-wide text-primary font-medium">
              <Calculator className="h-3.5 w-3.5" /> Salario Real Integrado
            </div>
            <div className="p-3 space-y-3">
              <div>
                <Label className="text-xs">Salario diario (base)</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="number" step="any" value={calc.salario} onChange={(e) => setCalc((c) => ({ ...c, salario: e.target.value }))} className="h-8 font-mono-num text-right rounded-sm" data-testid="fasar-calc-input" />
                  <Button size="sm" className="h-8 rounded-sm" onClick={runCalc} data-testid="fasar-calc-btn">Calcular</Button>
                </div>
              </div>
              {calc.result && (
                <table className="qc-table border border-border">
                  <tbody>
                    {[
                      ["salario_diario", "Salario diario", 2],
                      ["factor_integracion", "Factor de integración", 6],
                      ["salario_base_cotizacion", "SBC (IMSS)", 2],
                      ["dias_pagados", "Días pagados (Tp)", 2],
                      ["dias_laborados", "Días laborados (Tl)", 2],
                      ["factor_tp_tl", "Factor Tp/Tl", 6],
                      ["prestaciones_pct", "Prestaciones (%)", 2],
                    ].map(([k, l, d]) => (
                      <tr key={k}>
                        <td>{l}</td>
                        <td className="text-right font-mono-num">{fmt(calc.result[k], d)}</td>
                      </tr>
                    ))}
                    <tr className="bg-primary/10">
                      <td className="font-medium">Factor de Salario Real</td>
                      <td className="text-right font-mono-num font-medium text-primary">{fmt(calc.result.factor_salario_real, 6)}</td>
                    </tr>
                    <tr className="bg-primary/10">
                      <td className="font-medium">Salario real integrado</td>
                      <td className="text-right font-mono-num font-medium text-primary">$ {fmt(calc.result.salario_real_integrado, 6)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
              <p className="text-[11px] text-muted-foreground leading-snug">
                El versionado por año garantiza que un cambio de ley no altere presupuestos
                históricos. Los parámetros son totalmente editables.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-10 text-muted-foreground text-sm">Sin parámetros FASAR.</div>
      )}
    </div>
  );
}
